import AppContext, { IAppContext } from 'App/AppContext';
import { ActiveFrameworkAudit } from 'models/copilot/Processes/FrameworkAudit/copilotFrameworkAudit';
import { TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import { useContext, useEffect, useState } from 'react';
import { apiCopilotGetActiveFrameworkAudits } from 'services/Api/copilotService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';

const cacheTimeToLiveMs = 15000;
const pollIntervalMs = 10000;
// While nothing is running, still re-read this slowly: an audit started by a colleague (or in another
// tab) must eventually show up on a scene the user is just sitting on.
const idleRefreshIntervalMs = 60000;

// One shared store for every consumer: a dashboard can hold several standard widgets and they must not
// each call the API or each run their own timer. A fetch in flight is shared too, so mounting many
// consumers at once costs one request.
let cachedAudits: ActiveFrameworkAudit[] | undefined;
let cachedAtMs = 0;
let inFlight: Promise<ActiveFrameworkAudit[]> | undefined;
let pollTimer: ReturnType<typeof setInterval> | undefined;
let storeAppContext: IAppContext | undefined;
const listeners = new Set<() => void>();

const isRunning = (audit: ActiveFrameworkAudit): boolean => audit.status === TenantProcessStatus.InProgress;

const hasRunningAudit = (): boolean => (cachedAudits ?? []).some(isRunning);

const isSameList = (left: ActiveFrameworkAudit[], right: ActiveFrameworkAudit[]): boolean => {
  if (left.length !== right.length) return false;

  return left.every((audit, index) => audit.isEqual(right[index]));
};

const publish = (audits: ActiveFrameworkAudit[]): void => {
  cachedAtMs = Date.now();
  // Keep the array identity stable when nothing changed: consumers hold it in state, so an unchanged
  // poll must not re-render the scene, its active tab and every widget on it.
  if (cachedAudits && isSameList(cachedAudits, audits)) {
    return;
  }

  cachedAudits = audits;
  listeners.forEach((listener) => listener());
};

const load = (appContext: IAppContext, force: boolean): Promise<ActiveFrameworkAudit[]> => {
  storeAppContext = appContext;

  const isFresh = cachedAudits !== undefined && Date.now() - cachedAtMs < cacheTimeToLiveMs;
  if (!force && isFresh) return Promise.resolve(cachedAudits ?? []);
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      publish(await apiCopilotGetActiveFrameworkAudits(accessToken));

      return cachedAudits ?? [];
    } catch {
      // An enrichment, never a blocker: keep whatever the last successful read returned.
      return cachedAudits ?? [];
    } finally {
      inFlight = undefined;
    }
  })();

  return inFlight;
};

const startPolling = (): void => {
  if (pollTimer !== undefined) return;

  pollTimer = setInterval(() => {
    if (!storeAppContext) return;
    // A run in progress is followed closely; an idle tenant is only re-checked now and then.
    if (!hasRunningAudit() && Date.now() - cachedAtMs < idleRefreshIntervalMs) return;

    void load(storeAppContext, true);
  }, pollIntervalMs);
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  startPolling();

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && pollTimer !== undefined) {
      clearInterval(pollTimer);
      pollTimer = undefined;
    }
  };
};

export const getCachedActiveFrameworkAudits = (): ActiveFrameworkAudit[] => cachedAudits ?? [];

/**
 * Forces the next read of the running audits. For callers outside React (the copilot panel's framework
 * audit usage, right after starting or stopping a run) so the "AI audit running" markers appear in the
 * same visit that started the audit instead of after a navigation.
 */
export const notifyFrameworkAuditsChanged = (appContext: IAppContext): void => {
  void load(appContext, true);
};

/**
 * The framework audits running right now in the tenant, for the "AI audit running" markers outside the
 * Copilot panel (frameworks scene tabs, standard dashboard widgets).
 *
 * The run is unattended and survives navigation and the browser being closed, so these surfaces poll
 * the server rather than read panel state. The status is an enrichment: a failed read leaves the
 * previous answer and never surfaces an error.
 */
export const useActiveFrameworkAudits = (): { activeAudits: ActiveFrameworkAudit[] } => {
  const appContext = useContext(AppContext);
  const [activeAudits, setActiveAudits] = useState<ActiveFrameworkAudit[]>(getCachedActiveFrameworkAudits);

  const aiEnabled = hasUserFeature(appContext, FeatureTypes.AIAssistant);

  useEffect(() => {
    if (!aiEnabled) return;

    let cancelled = false;
    const sync = () => {
      if (!cancelled) setActiveAudits(getCachedActiveFrameworkAudits());
    };

    const unsubscribe = subscribe(sync);

    // The store may have moved between this render and this effect (another consumer's fetch landed).
    sync();
    void load(appContext, false).then(sync);

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [aiEnabled, appContext]);

  return { activeAudits };
};

export const isFrameworkAuditRunning = (audits: ActiveFrameworkAudit[], isoNormId: number | undefined): boolean => {
  if (!isoNormId) return false;

  return audits.some((audit) => audit.isoNormId === isoNormId && isRunning(audit));
};
